import { Box, Button, Flex, HStack, Icon, Progress, Spinner, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiSend } from "react-icons/fi";
import type { IconType } from "react-icons";

export interface NowPanelAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  icon?: "send";
  tone?: "danger";
}

const ACTION_ICONS: Record<NonNullable<NowPanelAction["icon"]>, IconType> = { send: FiSend };

export interface NowPanelProps {
  /** Already translated: the component runs no i18n over what it is handed. */
  status: ReactNode;
  detail?: ReactNode;
  progress?: { done: number; total: number } | null;
  action?: NowPanelAction | null;
  secondaryAction?: NowPanelAction | null;
  alternatives?: NowPanelAction[];
  label?: string;
  isLoading?: boolean;
}

export const NowPanel = ({
  status,
  detail,
  progress,
  action,
  secondaryAction,
  alternatives = [],
  label,
  isLoading = false,
}: NowPanelProps) => {
  const { t } = useTranslation();

  return (
    <Box
      mt="6"
      bg="smPrimarySofter"
      rounded="xl"
      border="1px solid"
      borderColor="smPrimarySofterBorder"
      px={{ base: "4", md: "6" }}
      py={{ base: "4", md: "5" }}
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
      >
        <Flex align="flex-start" gap={{ base: "3", md: "5" }} minW="0">
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="smMuted"
            textTransform="uppercase"
            letterSpacing="wider"
            pt={{ base: "0.5", md: "1" }}
            flexShrink={0}
          >
            {label ?? t("Now")}
          </Text>
          <Box minW="0">
            {isLoading ? (
              <HStack spacing="2" pt={{ base: "0.5", md: "1" }}>
                <Spinner size="sm" color="smPrimary" />
                <Text fontSize="sm" color="smMuted">
                  {status}
                </Text>
              </HStack>
            ) : (
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="smText">
                {status}
              </Text>
            )}
            {detail && (
              <Text fontSize="md" color="smMuted" mt="0.5">
                {detail}
              </Text>
            )}
            {progress && (
              <Progress
                value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
                aria-label={String(t("Progress"))}
                size="sm"
                rounded="full"
                colorScheme="teal"
                mt="3"
                maxW="sm"
                sx={{ "& > div": { borderRadius: "full" } }}
              />
            )}
            {alternatives.length > 0 && (
              <Flex wrap="wrap" columnGap="5" rowGap="1" mt="2">
                {alternatives.map((alternative) => (
                  <Button
                    key={alternative.label}
                    variant="link"
                    colorScheme="teal"
                    size="sm"
                    onClick={alternative.onClick}
                    isLoading={alternative.isLoading}
                    isDisabled={alternative.isDisabled}
                  >
                    {alternative.label}
                  </Button>
                ))}
              </Flex>
            )}
          </Box>
        </Flex>
        {(action || secondaryAction) && (
          <Flex direction={{ base: "column", sm: "row" }} gap="3" flexShrink={0}>
            {secondaryAction && (
              <Button
                variant="outline"
                colorScheme={secondaryAction.tone === "danger" ? "red" : "teal"}
                size="lg"
                onClick={secondaryAction.onClick}
                isLoading={secondaryAction.isLoading}
                isDisabled={secondaryAction.isDisabled}
              >
                {secondaryAction.label}
              </Button>
            )}
            {action && action.tone === "danger" && (
              <Button variant="outline" colorScheme="red" size="lg" onClick={action.onClick} isLoading={action.isLoading} isDisabled={action.isDisabled}>
                {action.label}
              </Button>
            )}
            {action && action.tone !== "danger" && (
              <Button
                colorScheme="teal"
                size="lg"
                leftIcon={action.icon && <Icon as={ACTION_ICONS[action.icon]} />}
                rightIcon={action.icon ? undefined : <Icon as={FiArrowRight} />}
                onClick={action.onClick}
                isLoading={action.isLoading}
                isDisabled={action.isDisabled}
              >
                {action.label}
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
